/**
 * What the overlay page needs BEFORE the React bundle runs, served statically from the route's
 * head(): transparency that does not depend on any JavaScript, and a guard for the window
 * features a locked-down WebView (Streamlabs mobile, some OBS builds) may refuse.
 *
 * Kept as plain strings/functions with no imports so the same code runs inline in <head> and
 * in node tests.
 */

/** Inline CSS in <head>: the page paints nothing even if the bundle never loads. */
export const OVERLAY_STATIC_CSS = [
    'html,body{background:transparent!important;margin:0;padding:0;overflow:hidden}',
    'body>div,#root{background:transparent!important}',
].join('');

type StorageLike = {getItem(k: string): string | null; setItem(k: string, v: string): void; removeItem(k: string): void; clear(): void; key(i: number): string | null; readonly length: number};

/**
 * Android WebViews with DOM storage disabled throw a SecurityError on the mere read of
 * window.localStorage — even `typeof localStorage` — and the app's providers read it at module
 * load, which is a white page before the overlay gets a chance. Replace an inaccessible storage
 * with an in-memory one so everything else runs; nothing on this page needs persistence.
 *
 * @returns which storages were shimmed, for the ?debug=1 panel.
 */
export function installStorageShim(win: {[k: string]: unknown}): string[] {
    const shimmed: string[] = [];
    for (const name of ['localStorage', 'sessionStorage']) {
        let ok = false;
        try {
            const s = win[name] as StorageLike | null;
            // Some WebViews expose the object but throw on use.
            if (s) { s.getItem('__sp'); ok = true; }
        } catch {
            ok = false;
        }
        if (ok) continue;
        const mem = new Map<string, string>();
        const shim: StorageLike = {
            getItem: (k) => (mem.has(k) ? mem.get(k)! : null),
            setItem: (k, v) => { mem.set(String(k), String(v)); },
            removeItem: (k) => { mem.delete(k); },
            clear: () => { mem.clear(); },
            key: (i) => Array.from(mem.keys())[i] ?? null,
            get length() { return mem.size; },
        };
        try {
            Object.defineProperty(win, name, {configurable: true, get: () => shim});
            shimmed.push(name);
        } catch {
            // Non-configurable and inaccessible: nothing more we can do; the debug panel will say.
        }
    }
    if (typeof win.matchMedia !== 'function') {
        win.matchMedia = () => ({matches: false, media: '', addEventListener() {}, removeEventListener() {}, addListener() {}, removeListener() {}, onchange: null, dispatchEvent: () => false});
        shimmed.push('matchMedia');
    }
    return shimmed;
}

/**
 * The inline <script> for the route head: installs the shim on window and records what it did
 * on window.__spOverlayBoot for the debug panel. Built from the function source so the two
 * never drift.
 */
export const OVERLAY_BOOT_SCRIPT = `(function(){try{var install=${installStorageShim.toString()};window.__spOverlayBoot={shimmed:install(window),at:Date.now(),errors:[]};}catch(e){window.__spOverlayBoot={shimmed:[],at:Date.now(),errors:[String(e)]};}window.addEventListener('error',function(e){try{(window.__spOverlayBoot.errors=window.__spOverlayBoot.errors||[]).push(String(e.message||e.error||e));}catch(_){}});window.addEventListener('unhandledrejection',function(e){try{(window.__spOverlayBoot.errors=window.__spOverlayBoot.errors||[]).push('promise: '+String(e.reason&&e.reason.message||e.reason));}catch(_){}});})();`;
